import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import * as clusterApi from '../../api/clusterApi';
import { Cluster, ClusterCreate, ClusterUpdate } from '../../api/clusterApi';

// Types
export interface ClusterState {
    clusters: Cluster[];
    deletedClusters: Cluster[];
    selectedCluster: Cluster | null;
    loadingCluster: boolean;
    error: string | null;
}

// Initial state
const initialState: ClusterState = {
    clusters: [],
    deletedClusters: [],
    selectedCluster: null,
    loadingCluster: false,
    error: null,
};

// Async thunks
export const fetchClusters = createAsyncThunk(
    'clusters/fetchAll',
    async (_, { rejectWithValue }) => {
        try {
            const response = await clusterApi.getAllClusters();
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch clusters');
        }
    }
);

export const fetchDeletedClusters = createAsyncThunk(
    'clusters/fetchDeleted',
    async (_, { rejectWithValue }) => {
        try {
            const response = await clusterApi.getDeletedClusters();
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch deleted clusters');
        }
    }
);

export const fetchClusterById = createAsyncThunk(
    'clusters/fetchById',
    async (id: string, { rejectWithValue }) => {
        try {
            const response = await clusterApi.getClusterById(id);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch cluster');
        }
    }
);

export const createCluster = createAsyncThunk(
    'clusters/create',
    async (clusterData: ClusterCreate, { rejectWithValue }) => {
        try {
            const response = await clusterApi.createCluster(clusterData);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to create cluster');
        }
    }
);

export const updateCluster = createAsyncThunk(
    'clusters/update',
    async ({ id, updateData }: { id: string; updateData: ClusterUpdate }, { rejectWithValue }) => {
        try {
            const response = await clusterApi.updateCluster(id, updateData);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to update cluster');
        }
    }
);

export const deleteCluster = createAsyncThunk(
    'clusters/delete',
    async (id: string, { rejectWithValue }) => {
        try {
            await clusterApi.deleteCluster(id);
            return id;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to delete cluster');
        }
    }
);

export const restoreCluster = createAsyncThunk(
    'clusters/restore',
    async (id: string, { rejectWithValue }) => {
        try {
            const response = await clusterApi.restoreCluster(id);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to restore cluster');
        }
    }
);

export const permanentlyDeleteCluster = createAsyncThunk(
    'clusters/permanentlyDelete',
    async (id: string, { rejectWithValue }) => {
        try {
            await clusterApi.permanentlyDeleteCluster(id);
            return id;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to permanently delete cluster');
        }
    }
);

export const duplicateCluster = createAsyncThunk(
    'clusters/duplicate',
    async (
        { id, newData }: { id: string; newData: Partial<ClusterCreate> },
        { rejectWithValue }
    ) => {
        try {
            const response = await clusterApi.duplicateCluster(id, newData);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to duplicate cluster');
        }
    }
);

// Slice
const clusterSlice = createSlice({
    name: 'clusters',
    initialState,
    reducers: {
        clearSelectedCluster: (state) => {
            state.selectedCluster = null;
        },
        clearClusterError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // Fetch all clusters
        builder.addCase(fetchClusters.pending, (state) => {
            state.loadingCluster = true;
            state.error = null;
        });
        builder.addCase(fetchClusters.fulfilled, (state, action: PayloadAction<Cluster[]>) => {
            state.loadingCluster = false;
            state.clusters = action.payload;
        });
        builder.addCase(fetchClusters.rejected, (state, action) => {
            state.loadingCluster = false;
            state.error = action.payload as string;
        });

        // Fetch deleted clusters
        builder.addCase(fetchDeletedClusters.pending, (state) => {
            state.loadingCluster = true;
            state.error = null;
        });
        builder.addCase(fetchDeletedClusters.fulfilled, (state, action: PayloadAction<Cluster[]>) => {
            state.loadingCluster = false;
            state.deletedClusters = action.payload;
        });
        builder.addCase(fetchDeletedClusters.rejected, (state, action) => {
            state.loadingCluster = false;
            state.error = action.payload as string;
        });

        // Fetch cluster by ID
        builder.addCase(fetchClusterById.pending, (state) => {
            state.loadingCluster = true;
            state.error = null;
        });
        builder.addCase(fetchClusterById.fulfilled, (state, action: PayloadAction<Cluster>) => {
            state.loadingCluster = false;
            state.selectedCluster = action.payload;
        });
        builder.addCase(fetchClusterById.rejected, (state, action) => {
            state.loadingCluster = false;
            state.error = action.payload as string;
        });

        // Create cluster
        builder.addCase(createCluster.pending, (state) => {
            state.loadingCluster = true;
            state.error = null;
        });
        builder.addCase(createCluster.fulfilled, (state, action: PayloadAction<Cluster>) => {
            state.loadingCluster = false;
            state.clusters.push(action.payload);
            state.selectedCluster = action.payload;
        });
        builder.addCase(createCluster.rejected, (state, action) => {
            state.loadingCluster = false;
            state.error = action.payload as string;
        });

        // Update cluster
        builder.addCase(updateCluster.pending, (state) => {
            state.loadingCluster = true;
            state.error = null;
        });

        // Update cluster (continued)
        builder.addCase(updateCluster.fulfilled, (state, action: PayloadAction<Cluster>) => {
            state.loadingCluster = false;

            // Update in the list
            const index = state.clusters.findIndex((c) => c._id === action.payload._id);
            if (index !== -1) {
                state.clusters[index] = action.payload;
            }

            // Update selected cluster if it's the same one
            if (state.selectedCluster && state.selectedCluster._id === action.payload._id) {
                state.selectedCluster = action.payload;
            }
        });
        builder.addCase(updateCluster.rejected, (state, action) => {
            state.loadingCluster = false;
            state.error = action.payload as string;
        });

        // Delete cluster
        builder.addCase(deleteCluster.pending, (state) => {
            state.loadingCluster = true;
            state.error = null;
        });
        builder.addCase(deleteCluster.fulfilled, (state, action: PayloadAction<string>) => {
            state.loadingCluster = false;
            state.clusters = state.clusters.filter((cluster) => cluster._id !== action.payload);

            if (state.selectedCluster && state.selectedCluster._id === action.payload) {
                state.selectedCluster = null;
            }
        });
        builder.addCase(deleteCluster.rejected, (state, action) => {
            state.loadingCluster = false;
            state.error = action.payload as string;
        });

        // Restore cluster
        builder.addCase(restoreCluster.pending, (state) => {
            state.loadingCluster = true;
            state.error = null;
        });
        builder.addCase(restoreCluster.fulfilled, (state, action: PayloadAction<Cluster>) => {
            state.loadingCluster = false;
            state.clusters.push(action.payload);
            state.deletedClusters = state.deletedClusters.filter(
                (cluster) => cluster._id !== action.payload._id
            );
        });
        builder.addCase(restoreCluster.rejected, (state, action) => {
            state.loadingCluster = false;
            state.error = action.payload as string;
        });

        // Permanently delete cluster
        builder.addCase(permanentlyDeleteCluster.pending, (state) => {
            state.loadingCluster = true;
            state.error = null;
        });
        builder.addCase(permanentlyDeleteCluster.fulfilled, (state, action: PayloadAction<string>) => {
            state.loadingCluster = false;
            state.deletedClusters = state.deletedClusters.filter(
                (cluster) => cluster._id !== action.payload
            );
        });
        builder.addCase(permanentlyDeleteCluster.rejected, (state, action) => {
            state.loadingCluster = false;
            state.error = action.payload as string;
        });

        // Duplicate cluster
        builder.addCase(duplicateCluster.pending, (state) => {
            state.loadingCluster = true;
            state.error = null;
        });
        builder.addCase(duplicateCluster.fulfilled, (state, action: PayloadAction<Cluster>) => {
            state.loadingCluster = false;
            state.clusters.push(action.payload);
        });
        builder.addCase(duplicateCluster.rejected, (state, action) => {
            state.loadingCluster = false;
            state.error = action.payload as string;
        });
    },
});

export const { clearSelectedCluster, clearClusterError } = clusterSlice.actions;

export default clusterSlice.reducer;